import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatCurrency, formatNumber, formatDate } from '../templates/export/formatters';

class CustomTemplateServiceExcelJS {
  constructor() {
    this.placeholders = {
      '[EMPRESA]': (data) => localStorage.getItem('empresa_nombre') || 'Mi Empresa',
      '[TELEFONO]': (data) => localStorage.getItem('empresa_telefono') || '',
      '[EMAIL]': (data) => localStorage.getItem('empresa_email') || '',
      '[WEB]': (data) => localStorage.getItem('empresa_web') || '',
      '[DIRECCION]': (data) => localStorage.getItem('empresa_direccion') || '',
      '[CLIENTE]': (data) => data.presupuesto?.cliente || '',
      '[OBRA]': (data) => data.presupuesto?.nombre_obra || '',
      '[UBICACION]': (data) => data.presupuesto?.ubicacion || '',
      '[FECHA]': (data) => formatDate(data.presupuesto?.fecha || new Date()),
      '[CODIGO]': (data) => data.presupuesto?.codigo || '',
      '[SUBTOTAL]': (data) => formatCurrency(data.totales?.costoDirecto || 0),
      '[IND]': (data) => data.porcentajes?.indirectos || 0,
      '[INDIRECTOS]': (data) => formatCurrency(data.totales?.indirectos || 0),
      '[FIN]': (data) => data.porcentajes?.financiamiento || 0,
      '[FINANCIAMIENTO]': (data) => formatCurrency(data.totales?.financiamiento || 0),
      '[UTI]': (data) => data.porcentajes?.utilidad || 0,
      '[UTILIDAD]': (data) => formatCurrency(data.totales?.utilidad || 0),
      '[IVA]': (data) => formatCurrency(data.totales?.iva || 0),
      '[TOTAL]': (data) => formatCurrency(data.totales?.total || 0)
    };
  }

  // Aplicar plantilla personalizada preservando TODOS los estilos
  async applyCustomTemplate(presupuesto, conceptos, porcentajes, templateData) {
    try {
      // Calcular totales
      const totales = this.calculateTotals(conceptos, porcentajes);

      // Datos para reemplazar
      const data = {
        presupuesto,
        conceptos,
        porcentajes,
        totales
      };

      // Si no hay datos del archivo guardado, usar plantilla por defecto
      if (!templateData.file || !templateData.file.buffer) {
        return this.exportDefault(data);
      }

      // Cargar el workbook desde el buffer guardado
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(templateData.file.buffer);

      const worksheet = workbook.getWorksheet(1);
      if (!worksheet) {
        throw new Error('No se encontró la hoja de trabajo');
      }

      // Encontrar la fila con [DATOS]
      let datosRow = null;
      let headerRow = null;

      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: false }, (cell) => {
          if (cell.value && cell.value.toString().includes('[DATOS]')) {
            datosRow = rowNumber;
            if (rowNumber > 1) {
              headerRow = worksheet.getRow(rowNumber - 1);
            }
          }
        });
      });

      // Reemplazar placeholders manteniendo formato
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        // No procesar la fila de [DATOS] aquí
        if (rowNumber === datosRow) return;

        row.eachCell({ includeEmpty: false }, (cell) => {
          if (cell.value && typeof cell.value === 'string') {
            let newValue = cell.value;

            // Reemplazar cada placeholder
            Object.keys(this.placeholders).forEach(placeholder => {
              if (newValue.includes(placeholder)) {
                const value = this.placeholders[placeholder](data);
                newValue = newValue.replace(placeholder, value);
              }
            });

            // Mantener el formato de la celda
            if (newValue !== cell.value) {
              const originalStyle = {
                font: cell.font ? { ...cell.font } : undefined,
                fill: cell.fill ? { ...cell.fill } : undefined,
                border: cell.border ? { ...cell.border } : undefined,
                alignment: cell.alignment ? { ...cell.alignment } : undefined,
                numFmt: cell.numFmt
              };

              cell.value = newValue;

              // Restaurar el estilo original
              if (originalStyle.font) cell.font = originalStyle.font;
              if (originalStyle.fill) cell.fill = originalStyle.fill;
              if (originalStyle.border) cell.border = originalStyle.border;
              if (originalStyle.alignment) cell.alignment = originalStyle.alignment;
              if (originalStyle.numFmt) cell.numFmt = originalStyle.numFmt;
            }
          }
        });
      });

      // Insertar conceptos donde está [DATOS]
      if (datosRow && headerRow) {
        // Obtener los estilos de la fila de [DATOS] para aplicar a los conceptos
        const templateRow = worksheet.getRow(datosRow);
        const templateStyles = [];

        templateRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          templateStyles[colNumber] = {
            font: cell.font ? { ...cell.font } : undefined,
            fill: cell.fill ? { ...cell.fill } : undefined,
            border: cell.border ? { ...cell.border } : undefined,
            alignment: cell.alignment ? { ...cell.alignment } : undefined,
            numFmt: cell.numFmt
          };
        });

        // Obtener headers para mapeo
        const headers = [];
        headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          headers[colNumber] = cell.value ? cell.value.toString().toLowerCase() : '';
        });

        // Eliminar la fila de [DATOS]
        worksheet.spliceRows(datosRow, 1);

        // Insertar los conceptos
        conceptos.forEach((concepto, index) => {
          const newRow = worksheet.insertRow(datosRow + index, []);

          // Mapear los datos según los headers
          headers.forEach((header, colNumber) => {
            if (!header) return;

            let value = '';
            let isNumber = false;

            if (header.includes('código') || header.includes('codigo')) {
              value = concepto.codigo || '';
            } else if (header.includes('descripción') || header.includes('descripcion')) {
              value = concepto.descripcion || '';
            } else if (header.includes('unidad')) {
              value = concepto.unidad || '';
            } else if (header.includes('cantidad')) {
              value = concepto.cantidad || 0;
              isNumber = true;
            } else if (header.includes('p.u') || header.includes('precio')) {
              value = concepto.precio_unitario || 0;
              isNumber = true;
            } else if (header.includes('importe')) {
              value = concepto.importe || 0;
              isNumber = true;
            }

            const cell = newRow.getCell(colNumber);
            cell.value = value;

            // Aplicar el estilo de la plantilla
            if (templateStyles[colNumber]) {
              if (templateStyles[colNumber].font) cell.font = templateStyles[colNumber].font;
              if (templateStyles[colNumber].fill) cell.fill = templateStyles[colNumber].fill;
              if (templateStyles[colNumber].border) cell.border = templateStyles[colNumber].border;
              if (templateStyles[colNumber].alignment) cell.alignment = templateStyles[colNumber].alignment;

              // Aplicar formato de número si corresponde
              if (isNumber && templateStyles[colNumber].numFmt) {
                cell.numFmt = templateStyles[colNumber].numFmt;
              }
            }
          });
        });
      }

      // Generar el archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `presupuesto_${presupuesto.codigo || 'custom'}_${new Date().getTime()}.xlsx`;

      saveAs(blob, fileName);

      return { success: true, fileName };

    } catch (error) {
      console.error('Error aplicando plantilla personalizada:', error);
      return { success: false, error: error.message };
    }
  }

  // Calcular totales
  calculateTotals(conceptos, porcentajes) {
    const costoDirecto = conceptos.reduce((sum, c) => sum + (c.importe || 0), 0);
    const indirectos = costoDirecto * (porcentajes.indirectos / 100);
    const financiamiento = costoDirecto * (porcentajes.financiamiento / 100);
    const utilidad = costoDirecto * (porcentajes.utilidad / 100);
    const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
    const iva = subtotal * (porcentajes.iva / 100);
    const total = subtotal + iva;

    return {
      costoDirecto,
      indirectos,
      financiamiento,
      utilidad,
      subtotal,
      iva,
      total
    };
  }

  // Exportación por defecto si no hay plantilla
  async exportDefault(data) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Presupuesto');

    // Título
    worksheet.mergeCells('A1:F1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = data.presupuesto.nombre_obra || 'PRESUPUESTO';
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Información del presupuesto
    worksheet.getCell('A3').value = 'Cliente:';
    worksheet.getCell('B3').value = data.presupuesto.cliente;
    worksheet.getCell('A4').value = 'Ubicación:';
    worksheet.getCell('B4').value = data.presupuesto.ubicacion;
    worksheet.getCell('A5').value = 'Fecha:';
    worksheet.getCell('B5').value = formatDate(data.presupuesto.fecha);

    // Headers de la tabla
    const headers = ['Código', 'Descripción', 'Unidad', 'Cantidad', 'P.U.', 'Importe'];
    const headerRow = worksheet.addRow([]);
    worksheet.addRow(headers);

    const headerRowNum = worksheet.lastRow.number;
    worksheet.getRow(headerRowNum).font = { bold: true };
    worksheet.getRow(headerRowNum).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Agregar conceptos
    data.conceptos.forEach(c => {
      worksheet.addRow([
        c.codigo,
        c.descripcion,
        c.unidad,
        c.cantidad,
        c.precio_unitario,
        c.importe
      ]);
    });

    // Agregar totales
    worksheet.addRow([]);
    const addTotalRow = (label, value) => {
      const row = worksheet.addRow(['', '', '', '', label, value]);
      row.getCell(5).font = { bold: true };
      row.getCell(6).numFmt = '"$"#,##0.00';
    };

    addTotalRow('Subtotal:', data.totales.costoDirecto);
    addTotalRow(`Indirectos (${data.porcentajes.indirectos}%):`, data.totales.indirectos);
    addTotalRow(`Financiamiento (${data.porcentajes.financiamiento}%):`, data.totales.financiamiento);
    addTotalRow(`Utilidad (${data.porcentajes.utilidad}%):`, data.totales.utilidad);
    addTotalRow('IVA (16%):', data.totales.iva);

    const totalRow = worksheet.addRow(['', '', '', '', 'TOTAL:', data.totales.total]);
    totalRow.getCell(5).font = { bold: true, size: 12 };
    totalRow.getCell(6).font = { bold: true, size: 12 };
    totalRow.getCell(6).numFmt = '"$"#,##0.00';

    // Ajustar anchos de columna
    worksheet.columns = [
      { width: 15 },  // Código
      { width: 50 },  // Descripción
      { width: 10 },  // Unidad
      { width: 12 },  // Cantidad
      { width: 15 },  // P.U.
      { width: 15 }   // Importe
    ];

    // Generar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `presupuesto_${data.presupuesto.codigo || 'export'}_${new Date().getTime()}.xlsx`;

    saveAs(blob, fileName);

    return { success: true, fileName };
  }

  // Guardar plantilla con el buffer completo
  async saveTemplate(name, file, buffer) {
    const templates = JSON.parse(localStorage.getItem('custom_excel_templates_v2') || '{}');

    templates[name] = {
      name,
      file: {
        name: file.name,
        buffer: Array.from(buffer), // Convertir a array para guardar en localStorage
        size: buffer.byteLength
      },
      createdAt: new Date().toISOString(),
      type: 'exceljs'
    };

    localStorage.setItem('custom_excel_templates_v2', JSON.stringify(templates));
    return true;
  }

  // Cargar archivo Excel para usar como plantilla
  async loadTemplateFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const buffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);

          // Verificar que el archivo es válido
          const worksheet = workbook.getWorksheet(1);
          if (!worksheet) {
            throw new Error('El archivo no contiene hojas de trabajo');
          }

          resolve({
            buffer: new Uint8Array(buffer),
            workbook,
            worksheetCount: workbook.worksheets.length,
            sheetNames: workbook.worksheets.map(ws => ws.name)
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsArrayBuffer(file);
    });
  }
}

// Singleton
const customTemplateServiceExcelJS = new CustomTemplateServiceExcelJS();
export default customTemplateServiceExcelJS;